import Phaser from 'phaser';
import { palette } from '../design/palette';
import { tokens } from '../design/tokens';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT, WORLD_HEIGHT } from '../dimensions';
import { BLOB_COUNT, initBlobs, packBlobsForShader, updateBlobs } from './LavaFieldSim';

interface BackgroundOptions {
  /** If true, gradient spans WORLD_HEIGHT (Game scene). If false, only LOGICAL_HEIGHT (Title). */
  fullWorld?: boolean;
}

// GPU lava field: a single fullscreen-quad Shader GameObject evaluates a Wyvill-style
// soft-object field (finite support) across BLOB_COUNT blobs per fragment and thresholds
// the result, producing the gooey silhouette. The blob positions/radii come from the JS
// sim in LavaFieldSim.ts and are fed in once per frame as a vec3[] uniform.
//
// Replaces the previous Canvas2D blur+contrast pipeline, which was the dominant per-frame
// cost on mobile (the canvas filter isn't GPU-accelerated on iOS Safari and a 720×1280
// RGBA buffer was re-uploaded every frame).

const FRAGMENT_SHADER = /* glsl */ `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

#define BLOB_COUNT ${BLOB_COUNT}

// xy: blob position in logical screen px (y=0 top of viewport).  z: effective radius.
uniform vec3 uBlobs[BLOB_COUNT];
uniform vec2 uResolution;
uniform vec3 uColorTop;
uniform vec3 uColorMid;
uniform vec3 uColorBottom;

// Phaser's ShaderQuad sets outTexCoord with (0,1) = top-left and (1,0) = bottom-right
// (GL convention with Y up). We convert back to top-down screen px for the math.
varying vec2 outTexCoord;

void main () {
  vec2 fragPx = vec2(outTexCoord.x * uResolution.x, (1.0 - outTexCoord.y) * uResolution.y);

  // Wyvill soft-object falloff with finite support: each blob contributes (1 - d^2/r^2)^2
  // for d <= r and zero past that. Crucial in a small viewport with many blobs — the
  // classic r^2/d^2 metaball field accumulates 16 long-range tails into a saturated soup.
  float field = 0.0;
  for (int i = 0; i < BLOB_COUNT; i++) {
    vec3 b = uBlobs[i];
    if (b.z < 0.5) continue;        // skip culled / radius-zero blobs
    vec2 d = fragPx - b.xy;
    float k = 1.0 - dot(d, d) / (b.z * b.z);
    if (k > 0.0) field += k * k;
  }

  // Threshold 0.35 puts an isolated blob's edge at d ≈ 0.77 r and adjacent blobs merge
  // when their fields combine to exceed it — gives goo-style blending. Smoothstep band
  // gives 1–2 px of free AA on the silhouette.
  float alpha = smoothstep(0.30, 0.40, field);

  // Viewport-locked vertical gradient: magenta at the top (cool), orange mid, yellow at the
  // bottom (hot). Matches the original which used a scrollFactor-0 canvas.
  float t = clamp(fragPx.y / uResolution.y, 0.0, 1.0);
  vec3 color = (t < 0.55)
    ? mix(uColorTop, uColorMid, t / 0.55)
    : mix(uColorMid, uColorBottom, (t - 0.55) / 0.45);

  // Wyvill goo: pre-multiplied alpha so the source-over blend with the world background
  // correctly hides the fragment where the field is below threshold.
  gl_FragColor = vec4(color * alpha, alpha);
}
`;

function hexStringToVec3(hex: string): Float32Array {
  const c = Phaser.Display.Color.HexStringToColor(hex);
  return new Float32Array([c.red / 255, c.green / 255, c.blue / 255]);
}

function createLavaField(scene: Phaser.Scene, initialViewBottom: number): void {
  const blobs = initBlobs(initialViewBottom - 30, initialViewBottom);

  // Canvas-renderer fallback: Phaser's ShaderCanvasRenderer is a no-op stub. Skip cleanly so
  // we don't depend on a render path that produces nothing. The Graphics gradient (below)
  // still covers the background.
  if (scene.sys.renderer.type !== Phaser.WEBGL) return;

  // Single Float32Array re-used every frame — Phaser's program wrapper diffs element-wise and
  // only calls gl.uniform3fv when at least one value actually changed.
  const flat = new Float32Array(BLOB_COUNT * 3);

  const shader = scene.add.shader({
    name: 'lava-field',
    fragmentSource: FRAGMENT_SHADER,
    initialUniforms: {
      // gl.getActiveUniform reports array uniforms as `name[0]`, and Phaser keys its
      // glUniforms map by that exact string — `uBlobs` alone silently misses.
      'uBlobs[0]': flat,
      uResolution: new Float32Array([LOGICAL_WIDTH, LOGICAL_HEIGHT]),
      uColorTop:    hexStringToVec3(tokens.colors.ui.accent),
      uColorMid:    hexStringToVec3(tokens.colors.ball.core),
      uColorBottom: hexStringToVec3(tokens.colors.target.core),
    },
  }, 0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT)
    .setOrigin(0, 0)
    .setScrollFactor(0)
    .setDepth(-15);

  const onUpdate = (time: number, deltaMs: number): void => {
    const dt = Math.min(deltaMs, 50) / 1000;  // clamp huge dt after pause/blur
    const scrollY = scene.cameras.main.scrollY;
    updateBlobs(blobs, dt, time, scrollY);
    packBlobsForShader(blobs, scrollY, flat);
    shader.setUniform('uBlobs[0]', flat);
  };
  scene.events.on('update', onUpdate);
  scene.events.once('shutdown', () => scene.events.off('update', onUpdate));
}

export function createBackground(scene: Phaser.Scene, opts: BackgroundOptions = {}): void {
  const totalHeight = opts.fullWorld ? WORLD_HEIGHT : LOGICAL_HEIGHT;

  // Vertical background gradient
  const g = scene.add.graphics();
  const top = palette.bgFrom;
  const bot = palette.bgTo;
  const steps = 40;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const r = lerpChannel(top, bot, t, 16);
    const gC = lerpChannel(top, bot, t, 8);
    const b = lerpChannel(top, bot, t, 0);
    const color = (r << 16) | (gC << 8) | b;
    g.fillStyle(color, 1);
    g.fillRect(0, (totalHeight * i) / steps, LOGICAL_WIDTH, totalHeight / steps + 1);
  }
  g.setDepth(-20);

  const viewBottom = opts.fullWorld ? WORLD_HEIGHT : LOGICAL_HEIGHT;
  createLavaField(scene, viewBottom);
}

function lerpChannel(from: number, to: number, t: number, shift: number): number {
  const a = (from >> shift) & 0xff;
  const b = (to >> shift) & 0xff;
  return Math.round(a + (b - a) * t);
}
